<template>
  <div>
    <summary-navigator-by-date
      :export="exportTotalsByUser"
      :report-name="reportName"
      @companychanged="companyChanged"
      @datechanged="dateChanged"
      @load:bamboo="(e) => (bambooIds = e)"
    >
      <dropdown
        :selected="adminSelectedOffice"
        @change="onOfficeChanged"
        :options="customOffices"
      />
    </summary-navigator-by-date>
    <p class="holiday-warning" v-if="hideTableEmpty && numHolidayMealsNotCounted > 0">
      {{ $t(`orderList.holidayWarningStart`) }}
      {{ numHolidayMealsNotCounted }}
      {{ $t(`orderList.holidayWarningEnd`) }}
    </p>
    <template>
      <div class="container">
        <el-row v-show="hideTableEmpty">
          <el-col :span="19">
            <table :aria-describedby="$t(`orderList.description`)" class="orders-by-user">
              <thead class="totals__by__user__table">
                <tr>
                  <th scope="col">
                    {{ $t(`orderList.bambooId`) }}
                  </th>
                  <th scope="col">
                    {{ $t(`orderList.nameUser`) }}
                  </th>
                  <th scope="col">
                    {{ $t(`orderList.email`) }}
                  </th>
                  <th scope="col">
                    {{ $t(`orderList.total`) }}
                  </th>
                </tr>
              </thead>
              <tbody>
                <tr
                  v-for="(userOrders, key) in ordersByUserNoHolidays"
                  :key="key"
                  :set="(userInfo = userOrders[0].userInfo)"
                >
                  <td class="order-bambo-id">
                    {{ bambooIds[userInfo.email] }}
                  </td>
                  <td class="order-name">{{ userInfo.name }}</td>
                  <td class="order-email">{{ userInfo.email }}</td>
                  <td class="order-total">{{ userOrders.length }}</td>
                </tr>
              </tbody>
            </table>
          </el-col>
        </el-row>
      </div>
    </template>
    <div>
      <el-tabs
        v-loading.fullscreen.lock="loading"
        :class="{ loading: loading }"
        tab-position="left"
        type="card"
      >
      </el-tabs>
    </div>
  </div>
</template>

<script>
import SummaryNavigatorByDate from '@/components/orders/SummaryNavigatorByDate';
import { DEFAULT_COMPANY } from '@/constants';
import { groupOrdersByPath } from '@/helpers';
import sortBy from 'lodash.sortby';
import isempty from 'lodash.isempty';
import { mapMutations, mapState } from 'vuex';
import Dropdown from '@/components/Dropdown';
import moment from 'moment';
import holidayFilter from '@/utils/holidayFilter';

import { findWeekOrdersByCountry, findWeekOrders } from '@/services/order';

const allCompaniesId = 1;
const gravityCompanyId = 2;
const wizelineCompanyId = 3;

const isWizeliner = (email) => email.includes('@wizeline');

export default {
  name: 'TotalsByUser',
  data() {
    return {
      bambooIds: {},
      company: DEFAULT_COMPANY,
      date: {},
      orders: {},
      loading: false,
      adminSelectedOffice: 0,
      strategies: {
        [gravityCompanyId]: (order) => isWizeliner(order.userInfo.email),
        [wizelineCompanyId]: (order) => !isWizeliner(order.userInfo.email),
      },
    };
  },
  components: {
    SummaryNavigatorByDate,
    Dropdown,
  },
  created() {
    this.filterOrdersByCompany();
  },
  computed: {
    ...mapState('events', { mealEvents: (state) => state.mealEvents }),
    ...mapState('weeklyMenu', { selectedWeek: (state) => state.selectedWeek }),
    ...mapState('offices', { offices: (state) => state.offices }),
    ...mapState('global', {
      adminSelectedCompany: 'adminSelectedCompany',
    }),
    customOffices() {
      const defaultOffice = {
        id: 0,
        name: 'All Mexican Offices',
        startDate: '',
        closeDate: '',
        country: 'Mexico',
      };
      let result;
      if (Array.isArray(this.offices)) {
        // eslint-disable-next-line no-undef
        const temporalArray = structuredClone(this.offices);
        temporalArray.unshift(defaultOffice);
        result = temporalArray;
      } else {
        result = new Array(defaultOffice);
      }
      return result;
    },
    companyOrders() {
      if (this.company === allCompaniesId) {
        return this.orders;
      }
      return this.filterOrdersByCompany();
    },
    officeName() {
      const office =
        !isempty(this.offices) && this.offices.find((o) => o.id === this.adminSelectedOffice);
      if (!office) return '';

      return office.name;
    },
    reportName() {
      const report = 'totals-by-user';
      return `${this.officeName}-${report}-${this.selectedWeek}.csv`;
    },
    getOrdersByUser() {
      const data = groupOrdersByPath(this.companyOrders, 'user');
      return sortBy(data, (item) => item[0].userInfo.name);
    },
    ordersByUserNoHolidays() {
      return this.getOrdersByUser
        .map((orders) => {
          return orders.filter((order) => holidayFilter(order.mealInfo.name));
        })
        .filter((orders) => orders.length > 0);
    },
    numHolidayMealsNotCounted() {
      let count = 0;
      this.getOrdersByUser.forEach((orders, index) => {
        const ordersAfterHolidayFilter = this.ordersByUserNoHolidays[index] || [];
        count += orders.length - ordersAfterHolidayFilter.length;
      });
      return count;
    },
    exportTotalsByUser() {
      const data = [];
      // Headers
      const header = [];
      header.push(this.$t(`orderList.bambooId`));
      header.push(this.$t(`orderList.nameUser`));
      header.push(this.$t(`orderList.email`));
      header.push(this.$t(`orderList.total`));
      data.push(header);
      Object.keys(this.ordersByUserNoHolidays).forEach((date) => {
        const orders = this.ordersByUserNoHolidays[date];
        // Body
        const ordersByDate = this.getUsersOrdersByUserName(orders);
        Object.keys(ordersByDate).forEach((ordersKey) => {
          const userRow = [];
          const userOrders = ordersByDate[ordersKey];
          const { name, email } = userOrders[0].userInfo;
          userRow.push(this.bambooIds[email]);
          userRow.push(name);
          userRow.push(email);
          userRow.push(userOrders.length);
          data.push(userRow);
        });
      });
      return data;
    },
    hideTableEmpty() {
      return !isempty(this.ordersByUserNoHolidays);
    },
  },
  methods: {
    ...mapMutations('orders', ['SET_LOADING']),
    getCountMeal(ordersByUser) {
      const mealGroup = groupOrdersByPath(ordersByUser, 'menuInfo.event');
      return mealGroup;
    },
    getUsersOrdersByUserName(orders) {
      return groupOrdersByPath(orders, 'userInfo.name');
    },
    onOfficeChanged(office) {
      this.adminSelectedOffice = office;
    },
    companyChanged(company) {
      this.company = company;
    },
    dateChanged(date) {
      this.date = date;
    },
    async getRecordOrders() {
      this.loading = true;
      if (this.adminSelectedOffice === 0) {
        const orders = await findWeekOrdersByCountry({
          country: 'Mexico',
          startDate: moment(this.date.startDate).format('YYYY-MM-DD'),
          endDate: moment(this.date.endDate).format('YYYY-MM-DD'),
        });

        this.orders = orders.data.findWeekOrdersByCountry;
      } else {
        const orders = await findWeekOrders({
          office: this.adminSelectedOffice,
          startDate: moment(this.date.startDate).format('YYYY-MM-DD'),
          endDate: moment(this.date.endDate).format('YYYY-MM-DD'),
        });
        this.orders = orders.data.findWeekOrders;
      }

      this.loading = false;
    },
    filterOrdersByCompany() {
      if (Object.keys(this.orders).length === 0) {
        return [];
      }
      const strategy = this.strategies[this.company];
      return this.orders.filter(strategy);
    },
  },
  watch: {
    date() {
      this.getRecordOrders();
    },
    adminSelectedOffice() {
      this.getRecordOrders();
    },
  },
};
</script>

<style scoped lang="scss">
@import '../../scss/variables';

.container {
  min-height: 300px;
  height: 100%;
}
.orders-by-user {
  width: 100%;
  margin-top: 2em;
  padding-right: 2em;
  .order-name,
  .order-email {
    width: 30%;
  }
  .order-diet,
  .order-total {
    width: 10%;
  }
  td {
    border: 1px solid $orders-by-user-border;
    padding: 0.1em 0.5em;
  }
}
.holiday-warning {
  color: red;
  font-size: 0.8em;
  margin-top: 1em;
}
</style>



// WEBPACK FOOTER //
// src/components/orders/TotalsByUser.vue